import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Icon,
  Input,
  Popup,
  SemanticCOLORS,
} from "semantic-ui-react";

import {
  getAccountOwnerOptions,
  useGetAccountOwners,
} from "hooks/useGetAccountOwners";
import {
  CarriersPopupFilter,
  CarrierStatus,
  carrierStatuses,
  getDefaultCarriersPopupFilter,
  CarriersFilter,
} from "hooks/useCarriersFilter";
import { CarriersOptions } from "hooks/useGetCarriersOptions";
import { useUsername } from "hooks/useUsername";
import { useGetLocationOptions } from "hooks/useGetLocationOptions";
import {
  getShipmentCountryOptions,
  useGetHistoricalShipmentCountries,
} from "hooks/useGetHistoricalShipmentCountries";
import { convertToFormDateString } from "pages/CoveragePlans/CoveragePlanForm/utils/coveragePlanApiToFormMappers";
import DateInput from "components/DateInput/DateInput";
import { trailerBuildTypeOptions } from "components/FleetCapabilities/FleetCapabilities.form";
import { laneCountryOptions } from "components/GeographicCoverage/GeographicCoverage.form";
import FilterMultiSelect from "./FilterMultiSelect";
import FilterSelect from "./FilterSelect";
import HistoricalShipmentCitySelect from "./HistoricalShipmentCitySelect";

import styles from "./CarrierFilterForm.module.scss";

export type CarrierFilterFormProps = {
  carriersFilter: CarriersFilter;
  setCarriersFilter: Dispatch<SetStateAction<CarriersFilter>>;
  closePopup: () => void;
  filterFormOptions: CarriersOptions;
  filterFormOptionsLoading: boolean;
  resetQuickCapacity: () => void;
};

export const statusToColorMap: Record<CarrierStatus, SemanticCOLORS> = {
  Active: "blue",
  Created: "green",
  Inactive: "grey",
};

export const defaultAccountOwners = {
  user: "Me",
  none: "No Owner",
};

const CarrierFilterForm = (props: CarrierFilterFormProps) => {
  const username = useUsername();
  const { data: accountOwners, isLoading: accountOwnersLoading } =
    useGetAccountOwners();
  const { locationOptions, locationOptionsLoading, debouncedLocationSearch } =
    useGetLocationOptions([
      props.carriersFilter.capacityOrigin,
      props.carriersFilter.capacityDestination,
    ]);
  const { data: shipmentCountries, isLoading: shipmentCountriesLoading } =
    useGetHistoricalShipmentCountries();

  const [popupFilter, setPopupFilter] = useState<CarriersPopupFilter>({
    myCarriers: props.carriersFilter.myCarriers,
    myFavoriteCarriers: props.carriersFilter.myFavoriteCarriers,
    unownedCarriers: props.carriersFilter.unownedCarriers,
    statuses: props.carriersFilter.statuses,
    accountOwners: props.carriersFilter.accountOwners,
    capacityOrigin: props.carriersFilter.capacityOrigin,
    capacityDestination: props.carriersFilter.capacityDestination,
    capacityPickupDate: props.carriersFilter.capacityPickupDate,
    trailerBuildTypeIds: props.carriersFilter.trailerBuildTypeIds,
    hasEvTrucks: props.carriersFilter.hasEvTrucks,
    hasHvoTrucks: props.carriersFilter.hasHvoTrucks,
    hasGpsTrackTrace: props.carriersFilter.hasGpsTrackTrace,
    shippeoOnboardedStatus: props.carriersFilter.shippeoOnboardedStatus,
    lf4ForMercedes: props.carriersFilter.lf4ForMercedes,
    vdiCertification: props.carriersFilter.vdiCertification,
    daimlerRulesCompliance: props.carriersFilter.daimlerRulesCompliance,
    toyotaRulesCompliance: props.carriersFilter.toyotaRulesCompliance,
    preferredLaneOriginCountryCodes:
      props.carriersFilter.preferredLaneOriginCountryCodes,
    preferredLaneDestinationCountryCodes:
      props.carriersFilter.preferredLaneDestinationCountryCodes,
    carrierCountries: props.carriersFilter.carrierCountries,
    minInsuranceAmount: props.carriersFilter.minInsuranceAmount,
    insuranceStatus: props.carriersFilter.insuranceStatus,
    transportLicenseStatus: props.carriersFilter.transportLicenseStatus,
    lastDeliveredFrom: props.carriersFilter.lastDeliveredFrom,
    lastDeliveredTo: props.carriersFilter.lastDeliveredTo,
    notActiveMoreThanSixMonths:
      props.carriersFilter.notActiveMoreThanSixMonths,
    shipmentOriginCountryCodes: props.carriersFilter.shipmentOriginCountryCodes,
    shipmentOriginCities: props.carriersFilter.shipmentOriginCities,
    shipmentDestinationCountryCodes:
      props.carriersFilter.shipmentDestinationCountryCodes,
    shipmentDestinationCities: props.carriersFilter.shipmentDestinationCities,
    shipmentRouteCompletedDateFrom:
      props.carriersFilter.shipmentRouteCompletedDateFrom,
    shipmentRouteCompletedDateTo:
      props.carriersFilter.shipmentRouteCompletedDateTo,
  });

  const applyCarriersFilter = () => {
    props.resetQuickCapacity();
    props.setCarriersFilter({
      ...props.carriersFilter,
      ...popupFilter,
    });
    props.closePopup();
  };

  const onChangePopupFilter = useCallback(
    (partialFilter: Partial<CarriersPopupFilter>) => {
      setPopupFilter((currentFilter) => ({
        ...currentFilter,
        ...partialFilter,
      }));
    },
    [setPopupFilter]
  );

  const defaultUserOptions = useMemo(() => {
    return [
      {
        key: "df81ec4d-7d14-4fb5-811d-731097af1ba2",
        value: defaultAccountOwners.user,
        text: username
          ? `${defaultAccountOwners.user} (${username})`
          : defaultAccountOwners.user,
        icon: "user circle",
      },
      {
        key: "bae4365e-72e7-4b07-8b94-045e2ac3167f",
        value: defaultAccountOwners.none,
        text: defaultAccountOwners.none,
        icon: "ban",
      },
    ];
  }, [username]);

  const accountOwnersValue = useMemo(() => {
    const accountOwners: string[] = [];

    if (popupFilter.myCarriers) {
      accountOwners.push(defaultAccountOwners.user);
    }

    if (popupFilter.unownedCarriers) {
      accountOwners.push(defaultAccountOwners.none);
    }

    accountOwners.push(...popupFilter.accountOwners.map((owner) => owner.key));

    return accountOwners;
  }, [
    popupFilter.myCarriers,
    popupFilter.unownedCarriers,
    popupFilter.accountOwners,
  ]);

  const accountOwnerOptions = useMemo(
    () => getAccountOwnerOptions(accountOwners, popupFilter.accountOwners),
    [accountOwners, popupFilter.accountOwners]
  );

  const shipmentOriginCountryOptions = useMemo(
    () =>
      getShipmentCountryOptions(
        shipmentCountries.originCountryCodes,
        popupFilter.shipmentOriginCountryCodes
      ),
    [
      shipmentCountries.originCountryCodes,
      popupFilter.shipmentOriginCountryCodes,
    ]
  );

  const shipmentDestinationCountryOptions = useMemo(
    () =>
      getShipmentCountryOptions(
        shipmentCountries.destinationCountryCodes,
        popupFilter.shipmentDestinationCountryCodes
      ),
    [
      shipmentCountries.destinationCountryCodes,
      popupFilter.shipmentDestinationCountryCodes,
    ]
  );

  return (
    <Form onSubmit={applyCarriersFilter}>
      <div className={styles.popupContainer}>
        <div className={styles.filterHeader}>
          <h2>Carrier Filter</h2>
          <Icon link size="large" name="close" onClick={props.closePopup} />
        </div>

        <div className={styles.scrollContainer}>
          <div>
            <div className={styles.header}>
              Historical Shipments{" "}
              <Popup
                wide
                trigger={<Icon circular name="question" size="small" />}
                content="Results will show carriers with at least one Route complete, Ready for billing, Processing or Completed shipment matching all selected fields"
                offset={[-10, 0]}
              />
            </div>
            <div className={styles.inlineDropdowns}>
              <FilterMultiSelect
                name="shipmentOriginCountryCodes"
                label="Origin Country"
                ariaLabel="Shipment Origin Country"
                search
                options={shipmentOriginCountryOptions}
                loading={shipmentCountriesLoading}
                value={popupFilter.shipmentOriginCountryCodes}
                onChange={(shipmentOriginCountryCodes) =>
                  onChangePopupFilter({ shipmentOriginCountryCodes })
                }
              />

              <HistoricalShipmentCitySelect
                name="shipmentOriginCities"
                label="Origin City"
                ariaLabel="Shipment Origin City"
                citiesUrl="/api/carriers/historicalShipmentOriginCities"
                countryCodes={popupFilter.shipmentOriginCountryCodes}
                searchMinLength={
                  props.filterFormOptions.historicalShipmentCitySearchMinLength
                }
                value={popupFilter.shipmentOriginCities}
                onChange={(shipmentOriginCities) =>
                  onChangePopupFilter({ shipmentOriginCities })
                }
              />

              <FilterMultiSelect
                name="shipmentDestinationCountryCodes"
                label="Destination Country"
                ariaLabel="Shipment Destination Country"
                search
                options={shipmentDestinationCountryOptions}
                loading={shipmentCountriesLoading}
                value={popupFilter.shipmentDestinationCountryCodes}
                onChange={(shipmentDestinationCountryCodes) =>
                  onChangePopupFilter({ shipmentDestinationCountryCodes })
                }
              />

              <HistoricalShipmentCitySelect
                name="shipmentDestinationCities"
                label="Destination City"
                ariaLabel="Shipment Destination City"
                citiesUrl="/api/carriers/historicalShipmentDestinationCities"
                countryCodes={popupFilter.shipmentDestinationCountryCodes}
                searchMinLength={
                  props.filterFormOptions.historicalShipmentCitySearchMinLength
                }
                value={popupFilter.shipmentDestinationCities}
                onChange={(shipmentDestinationCities) =>
                  onChangePopupFilter({ shipmentDestinationCities })
                }
              />

              <Form.Field inline>
                <label htmlFor="shipmentRouteCompletedDateFrom">
                  Route Complete Date From
                </label>
                <DateInput
                  id="shipmentRouteCompletedDateFrom"
                  name="shipmentRouteCompletedDateFrom"
                  aria-label="Shipment Route Complete Date From"
                  max={popupFilter.shipmentRouteCompletedDateTo}
                  value={popupFilter.shipmentRouteCompletedDateFrom}
                  onChange={(value) => {
                    onChangePopupFilter({
                      shipmentRouteCompletedDateFrom: value,
                    });
                  }}
                />
              </Form.Field>

              <Form.Field inline>
                <label htmlFor="shipmentRouteCompletedDateTo">
                  Route Complete Date To
                </label>
                <DateInput
                  id="shipmentRouteCompletedDateTo"
                  name="shipmentRouteCompletedDateTo"
                  aria-label="Shipment Route Complete Date To"
                  min={popupFilter.shipmentRouteCompletedDateFrom}
                  value={popupFilter.shipmentRouteCompletedDateTo}
                  onChange={(value) => {
                    onChangePopupFilter({
                      shipmentRouteCompletedDateTo: value,
                    });
                  }}
                />
              </Form.Field>
            </div>
          </div>

          <div>
            <div className={styles.header}>Quick Filters</div>
            <Checkbox
              id="myFavoriteCarriers"
              name="myFavoriteCarriers"
              label="My Favorite Carriers"
              checked={popupFilter.myFavoriteCarriers}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ myFavoriteCarriers: checked });
              }}
            />

            <Checkbox
              id="notActiveMoreThanSixMonths"
              name="notActiveMoreThanSixMonths"
              label="Not Active More Than 6 Months"
              checked={popupFilter.notActiveMoreThanSixMonths}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ notActiveMoreThanSixMonths: checked });
              }}
            />
          </div>

          <div>
            <div className={styles.header}>Carrier Details</div>
            <div className={styles.inlineDropdowns}>
              <FilterMultiSelect
                name="statuses"
                label="Carrier Status"
                options={carrierStatuses.map((status) => ({
                  key: status,
                  text: status,
                  value: status,
                  label: {
                    color: statusToColorMap[status] ?? "grey",
                    empty: true,
                    circular: true,
                  },
                }))}
                value={popupFilter.statuses}
                onChange={(statuses) => onChangePopupFilter({ statuses })}
              />

              <FilterMultiSelect
                name="carrierCountries"
                label="Country"
                ariaLabel="Carrier Country"
                search
                options={props.filterFormOptions.carrierCountryOptions}
                loading={props.filterFormOptionsLoading}
                value={popupFilter.carrierCountries}
                onChange={(carrierCountries) =>
                  onChangePopupFilter({ carrierCountries })
                }
              />

              <Form.Field inline>
                <label htmlFor="accountOwner">Account Owner</label>
                <Dropdown
                  clearable
                  selection
                  search
                  deburr
                  multiple
                  id="accountOwner"
                  name="accountOwner"
                  aria-label="Account Owner"
                  placeholder="Account Owner"
                  options={[...defaultUserOptions, ...accountOwnerOptions]}
                  loading={accountOwnersLoading}
                  value={accountOwnersValue}
                  onChange={(_, { value }) => {
                    const ownersValue = value as string[];
                    const accountOwners = accountOwnerOptions.filter(
                      (accountOwnerOption) =>
                        ownersValue.includes(accountOwnerOption.key)
                    );

                    onChangePopupFilter({
                      myCarriers: ownersValue.includes(
                        defaultAccountOwners.user
                      ),
                      unownedCarriers: ownersValue.includes(
                        defaultAccountOwners.none
                      ),
                      accountOwners,
                    });
                  }}
                />
              </Form.Field>
            </div>
          </div>

          <div>
            <div className={styles.header}>Fleet & Technology</div>
            <div className={styles.inlineDropdowns}>
              <FilterMultiSelect
                name="trailerBuildTypeIds"
                label="Trailer Build Type"
                options={trailerBuildTypeOptions.map((opt) => ({
                  key: opt.id,
                  text: opt.name,
                  value: opt.id,
                }))}
                value={popupFilter.trailerBuildTypeIds}
                onChange={(trailerBuildTypeIds) =>
                  onChangePopupFilter({ trailerBuildTypeIds })
                }
              />
            </div>
            <Checkbox
              id="hasEvTrucks"
              name="hasEvTrucks"
              label="EV Trucks"
              checked={popupFilter.hasEvTrucks}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ hasEvTrucks: checked });
              }}
              style={{ marginBottom: 6 }}
            />
            <Checkbox
              id="hasHvoTrucks"
              name="hasHvoTrucks"
              label="HVO Trucks"
              checked={popupFilter.hasHvoTrucks}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ hasHvoTrucks: checked });
              }}
              style={{ marginBottom: 6 }}
            />
            <Checkbox
              id="hasGpsTrackTrace"
              name="hasGpsTrackTrace"
              label="GPS / Track & Trace"
              checked={popupFilter.hasGpsTrackTrace}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ hasGpsTrackTrace: checked });
              }}
              style={{ marginBottom: 6 }}
            />
            <FilterSelect
              name="shippeoOnboardedStatus"
              label="Shippeo Onboarded"
              placeholder="All"
              options={[
                { key: "true", text: "Onboarded", value: "true" },
                { key: "false", text: "Not onboarded", value: "false" },
              ]}
              value={popupFilter.shippeoOnboardedStatus}
              onChange={(shippeoOnboardedStatus) =>
                onChangePopupFilter({ shippeoOnboardedStatus })
              }
            />
          </div>

          <div>
            <div className={styles.header}>Certification</div>
            <Checkbox
              id="lf4ForMercedes"
              name="lf4ForMercedes"
              label="LF4 for Mercedes"
              checked={popupFilter.lf4ForMercedes}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ lf4ForMercedes: checked });
              }}
              style={{ marginBottom: 6 }}
            />
            <Checkbox
              id="vdiCertification"
              name="vdiCertification"
              label="VDI Certification"
              checked={popupFilter.vdiCertification}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ vdiCertification: checked });
              }}
              style={{ marginBottom: 6 }}
            />
            <Checkbox
              id="daimlerRulesCompliance"
              name="daimlerRulesCompliance"
              label="Daimler Rules Compliance"
              checked={popupFilter.daimlerRulesCompliance}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ daimlerRulesCompliance: checked });
              }}
              style={{ marginBottom: 6 }}
            />
            <Checkbox
              id="toyotaRulesCompliance"
              name="toyotaRulesCompliance"
              label="Toyota Rules Compliance"
              checked={popupFilter.toyotaRulesCompliance}
              onChange={(_, { checked }) => {
                onChangePopupFilter({ toyotaRulesCompliance: checked });
              }}
            />
          </div>

          <div>
            <div className={styles.header}>Routes & Lanes</div>
            <div className={styles.inlineDropdowns}>
              <FilterMultiSelect
                name="preferredLaneOriginCountryCodes"
                label="Preferred Origin"
                placeholder="Country Codes"
                search
                options={laneCountryOptions}
                value={popupFilter.preferredLaneOriginCountryCodes}
                onChange={(preferredLaneOriginCountryCodes) =>
                  onChangePopupFilter({ preferredLaneOriginCountryCodes })
                }
              />

              <FilterMultiSelect
                name="preferredLaneDestinationCountryCodes"
                label="Preferred Destination"
                placeholder="Country Codes"
                search
                options={laneCountryOptions}
                value={popupFilter.preferredLaneDestinationCountryCodes}
                onChange={(preferredLaneDestinationCountryCodes) =>
                  onChangePopupFilter({ preferredLaneDestinationCountryCodes })
                }
              />
            </div>
          </div>

          <div>
            <div className={styles.header}>Insurance</div>
            <div className={styles.inlineDropdowns}>
              <Form.Field inline>
                <label htmlFor="minInsuranceAmount">Min Amount</label>
                <Input
                  id="minInsuranceAmount"
                  name="minInsuranceAmount"
                  aria-label="Minimum Insurance Amount"
                  placeholder="Min insurance amount"
                  type="number"
                  min={0}
                  value={popupFilter.minInsuranceAmount}
                  onChange={(_, { value }) => {
                    onChangePopupFilter({ minInsuranceAmount: value });
                  }}
                />
              </Form.Field>
              <FilterSelect
                name="insuranceStatus"
                label="Status"
                ariaLabel="Insurance Status"
                placeholder="All"
                options={[
                  { key: "All", text: "All", value: "All" },
                  { key: "Active", text: "Active", value: "Active" },
                  { key: "Expired", text: "Expired", value: "Expired" },
                ]}
                value={popupFilter.insuranceStatus}
                onChange={(insuranceStatus) =>
                  onChangePopupFilter({ insuranceStatus })
                }
              />
              <FilterSelect
                name="transportLicenseStatus"
                label="Transport License"
                ariaLabel="Transport License Status"
                placeholder="All"
                options={[
                  { key: "All", text: "All", value: "All" },
                  { key: "Active", text: "Active", value: "Active" },
                  { key: "Expired", text: "Expired", value: "Expired" },
                ]}
                value={popupFilter.transportLicenseStatus}
                onChange={(transportLicenseStatus) =>
                  onChangePopupFilter({ transportLicenseStatus })
                }
              />
            </div>
          </div>

          <div>
            <div className={styles.header}>Last Delivered</div>
            <div className={styles.inlineDropdowns}>
              <Form.Field inline>
                <label htmlFor="lastDeliveredFrom">From</label>
                <DateInput
                  id="lastDeliveredFrom"
                  name="lastDeliveredFrom"
                  aria-label="Last Delivered From"
                  max={popupFilter.lastDeliveredTo}
                  value={popupFilter.lastDeliveredFrom}
                  onChange={(value) => {
                    onChangePopupFilter({ lastDeliveredFrom: value });
                  }}
                />
              </Form.Field>

              <Form.Field inline>
                <label htmlFor="lastDeliveredTo">To</label>
                <DateInput
                  id="lastDeliveredTo"
                  name="lastDeliveredTo"
                  aria-label="Last Delivered To"
                  min={popupFilter.lastDeliveredFrom}
                  value={popupFilter.lastDeliveredTo}
                  onChange={(value) => {
                    onChangePopupFilter({ lastDeliveredTo: value });
                  }}
                />
              </Form.Field>
            </div>
          </div>

          <div>
            <div className={styles.header}>
              Available Capacity{" "}
              <Popup
                wide
                trigger={<Icon circular name="question" size="small" />}
                content="Results will be filtered within 1 day of pickup date and 100 km of origin and destination"
                offset={[-10, 0]}
              />
            </div>

            <div className={styles.inlineDropdowns}>
              <Form.Field inline>
                <label htmlFor="capacityPickupDate">Pickup Date</label>
                <DateInput
                  id="capacityPickupDate"
                  name="capacityPickupDate"
                  aria-label="Capacity Pickup Date"
                  min={convertToFormDateString(new Date().toISOString())}
                  value={popupFilter.capacityPickupDate}
                  onChange={(value) => {
                    onChangePopupFilter({ capacityPickupDate: value });
                  }}
                />
              </Form.Field>

              <FilterSelect
                name="capacityOrigin"
                label="Origin"
                ariaLabel="Capacity Origin"
                search
                upward
                options={locationOptions}
                loading={locationOptionsLoading}
                onSearchChange={(_event, { searchQuery }) =>
                  debouncedLocationSearch(searchQuery.trim())
                }
                value={popupFilter.capacityOrigin}
                onChange={(capacityOrigin) =>
                  onChangePopupFilter({ capacityOrigin })
                }
              />

              <FilterSelect
                name="capacityDestination"
                label="Destination"
                ariaLabel="Capacity Destination"
                search
                upward
                options={locationOptions}
                loading={locationOptionsLoading}
                onSearchChange={(_event, { searchQuery }) =>
                  debouncedLocationSearch(searchQuery.trim())
                }
                value={popupFilter.capacityDestination}
                onChange={(capacityDestination) =>
                  onChangePopupFilter({ capacityDestination })
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.filterFooter}>
          <Button
            type="button"
            onClick={() => setPopupFilter(getDefaultCarriersPopupFilter())}
          >
            <Icon name="redo" />
            Clear Filter
          </Button>

          <Button type="submit" color="blue">
            <Icon name="search" />
            Apply Filter
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default CarrierFilterForm;
